import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

const LANGUAGES = [
  { code: 'en', labelKey: 'common.english' },
  { code: 'es', labelKey: 'common.spanish' },
];

/*
|------------------------------------------------------------------------------
| Locale switcher. Persists the choice via the LanguageDetector caches
| (localStorage `nacis.locale`). Two appearance variants:
|   - variant="light"  → dark-on-light (marketing header)
|   - variant="dark"   → light-on-dark (dashboard sidebar / launch page)
|------------------------------------------------------------------------------
*/

export default function LanguageSwitcher({ variant = 'light', className }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const active = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const styles = variant === 'dark'
    ? {
        trigger: 'text-slate-200 hover:bg-white/10 hover:text-white',
        menuBg:  'bg-slate-900 border-white/10',
        item:    'text-slate-200 hover:bg-white/5',
      }
    : {
        trigger: 'text-slate-700 hover:bg-slate-100',
        menuBg:  'bg-white border-slate-200',
        item:    'text-slate-700 hover:bg-slate-50',
      };

  function pick(code) {
    i18n.changeLanguage(code);
    setOpen(false);
  }

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={t('common.language')}
        className={clsx('inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition', styles.trigger)}
      >
        <Globe className="h-4 w-4" strokeWidth={2.2} />
        <span className="uppercase">{active}</span>
        <ChevronDown className={clsx('h-3.5 w-3.5 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className={clsx('absolute right-0 top-full z-30 mt-2 w-44 rounded-lg border p-1 shadow-lg', styles.menuBg)}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => pick(lang.code)}
              className={clsx('flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition', styles.item)}
            >
              <span>{t(lang.labelKey)}</span>
              {active === lang.code && <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2.4} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
